import { PatientInput } from '../../components/forms/create-user-form/create-patient-form/patientFormTypes';
import {
  ClinicianInput,
  UserTypes,
} from '../../components/forms/create-user-form/CreateUserUtils';
import { AdditionalUserInfoDto, UserInfoDto } from '../backendType';

/**
 * Canonical form of a login email: trimmed and lowercased.
 *
 * The address an admin types here becomes the Cognito sign-in alias, and the pools are
 * case-sensitive. Sign-in and forgot-password both lowercase before querying
 * (`AppWithAuth.tsx`), so an invite created as `Foo@x.com` yields an account that can
 * neither be signed into nor reset — and the reset failure is silent by design.
 *
 * The backend normalizes too (`Utils.NormalizeEmail`, applied at the DTO boundary), and
 * that is the authoritative guard because the portal is not the only caller. This is
 * defence in depth, and it keeps what the admin sees consistent with what is stored.
 */
export function normalizeLoginEmail(email: string): string {
  return email.trim().toLowerCase();
}

export function toPatientInfoDto(
  userInput: PatientInput,
  emrPatientId?: string | undefined,
): UserInfoDto {
  const email = normalizeLoginEmail(
    userInput.isTestUser
      ? `${userInput.firstName.trim().replace(/\s+/g, '.')}@lakecarmel.com`
      : userInput.email,
  );
  const userInfoDto: UserInfoDto = {
    email,
    name: userInput.firstName,
    last_name: userInput.lastName,
    middle_name: userInput.middleName,
    role: UserTypes.Patient,
    additional_user_info: {
      height: userInput.height,
      is_test_user: userInput.isTestUser,
      date_of_birth: userInput.dateOfBirth,
      gender: userInput.gender,
      weight: userInput.weight,
      icd10_desc: userInput.icd10Desc,
      icd10_codes: userInput.icd10Codes,
      assessment: userInput.assessment,
      primary_complaint: userInput.primaryComplaint,
      current_level_of_function: userInput.currentLevelOfFunction,
      diagnosis: userInput.diagnosis,
      ...userInput.clinicalInfo,
    },
  };
  if (emrPatientId) {
    userInfoDto.emr_user_id = emrPatientId;
  }
  if (userInput.isTestUser && userInfoDto.additional_user_info) {
    userInfoDto.additional_user_info.surgery_date = userInput.surgeryDate;
    userInfoDto.additional_user_info.surgical_leg = userInput.surgicalLeg;
    userInfoDto.additional_user_info.medical_condition = userInput.medicalCondition;
    userInfoDto.additional_user_info.ethnicity = userInput.ethnicity;
  }
  return userInfoDto;
}

export function toClinicianInfoDto(userInput: ClinicianInput) {
  const userInfoDto: UserInfoDto = {
    email: normalizeLoginEmail(userInput.email),
    role: UserTypes.Clinician,
    name: userInput.firstName,
    last_name: userInput.lastName,
    additional_user_info: {
      prefix: userInput.prefix,
      is_test_user: userInput.isTestUser,
      credentials: userInput.credentials,
      license_number: userInput.licenseNumber,
    },
  };
  return userInfoDto;
}

/**
 * The body for UPDATING a clinician: the edit form's fields merged onto what is already stored.
 *
 * The backend replaces `additional_user_info` wholesale on update, and the edit form only knows
 * its own fields (and starts `is_test_user` at false). Sending the form's bag alone therefore
 * erased every other stored key (phone, anything added later) and reset the test-user flag of
 * whoever was edited. The stored flag always wins: the edit form has no control for it.
 */
export function toClinicianUpdateDto(
  userInput: ClinicianInput,
  stored: AdditionalUserInfoDto | undefined,
): UserInfoDto {
  const dto = toClinicianInfoDto(userInput);
  dto.additional_user_info = {
    ...(stored ?? {}),
    ...dto.additional_user_info,
    is_test_user: stored?.is_test_user ?? dto.additional_user_info?.is_test_user,
  };
  return dto;
}
